/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Dropdown,
  Option,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Add24Regular,
  Pen24Regular,
  Book24Regular,
  HatGraduation24Regular,
  NavigationUnreadRegular,
  ArrowSyncRegular,
  CoinStackRegular,
} from "@fluentui/react-icons";
import Projektdaten from "./projektdaten";
import type { IDataItem } from "../../models/IDataItem";
import { DataService } from "../../services/dataService";
import { useMsal } from "@azure/msal-react";
import { getCurrentRole } from "../../utils/role";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalNone}`,
    color: tokens.colorNeutralForeground1,
  },
  content: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
    minHeight: 0,
  },
  heading: {
    fontSize: "1.3rem",
    fontWeight: 600,
    marginBottom: tokens.spacingVerticalM,
  },
  helperText: {
    fontSize: "0.9rem",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM,
    lineHeight: "1.9",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalL,
    width: "100%",
    maxWidth: "760px",
    paddingTop: "20px",
  },
  formatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalL,
    width: "100%",
    maxWidth: "760px",
    paddingTop: "20px",
  },
  iconWrap: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    marginBottom: tokens.spacingVerticalXS,
    fontWeight: 600,
    lineHeight: "1.2",
    marginTop: tokens.spacingVerticalXS,
  },
  description: {
    maxWidth: "30ch",
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.45,
    whiteSpace: "pre-line",
    marginTop: tokens.spacingVerticalXS,
  },
  actionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    width: "100%",
    maxWidth: "760px",
    marginTop: "auto",
    paddingTop: tokens.spacingVerticalXL,
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: tokens.spacingVerticalM,
    },
  },
  actionButton: {
    width: "250px",
    minWidth: "250px",
    flex: "0 0 250px",
    "@media (max-width: 768px)": {
      width: "100%",
      minWidth: "0",
      flexBasis: "auto",
    },
  },
});
const cardBaseStyle: React.CSSProperties = {
  minHeight: "200px",
  width: "100%",
  borderRadius: "12px",
  border: `1px solid ${tokens.colorNeutralStroke2}`,
  backgroundColor: tokens.colorNeutralBackground1,
  boxShadow: "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
  paddingTop: "10px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxSizing: "border-box",
};

const selectedCardStyle: React.CSSProperties = {
  ...cardBaseStyle,
  borderColor: "#0078d4",
  boxShadow: "0 0 0 1px #0078d4",
  backgroundColor: "#eef7ff",
};

const badgeStyles: Record<string, React.CSSProperties> = {
  add: {
    backgroundColor: "#dfeeff",
    color: tokens.colorBrandForeground1,
  },
  edit: {
    backgroundColor: "#dff6ea",
    color: "#1d9f62",
  },
  cycle: {
    backgroundColor: "#dff5d9",
    color: "#2e7d32",
  },
  acyclic: {
    backgroundColor: "#e8f4ff",
    color: "#2563eb",
  },
  studi: {
    backgroundColor: "#f0e6ff",
    color: "#8b3dcb",
  },
  lehrgang: {
    backgroundColor: "#ffe7d9",
    color: "#df6d38",
  },
  coin: {
    backgroundColor: "#2e7d32",
    color: "#58cf6c",
  },
  // micro: {
  //   backgroundColor: "#dffaf1",
  //   color: "#1db88c",
  // },
};

type ProjectType = "Neuentwicklung" | "Überarbeitung";
type ProjectFormat = "Studiengang" | "Lehrgang";
type RevisionType = "Zyklisch" | "Azyklisch";
type BezugType = "Projekt" | "Studiengang";

interface IProjektanlageProps {
  onClose?: () => void;
  onPageChange?: (page: "initial" | "neuanlage") => void;
}

const Projektanlage: React.FC<IProjektanlageProps> = ({
  onClose,
  onPageChange,
}) => {
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const styles = useStyles();
  const [selectedType, setSelectedType] =
    useState<ProjectType>("Neuentwicklung");
  const [selectedFormat, setSelectedFormat] =
    useState<ProjectFormat>("Studiengang");
  const [selectedRevisionType, setSelectedRevisionType] =
    useState<RevisionType>("Zyklisch");
  const [page, setPage] = useState<string>("initial");
  const [selectedBezug, setSelectedBezug] = useState<BezugType>("Projekt");
  const [projekte, setProjekte] = useState([] as IDataItem[]);
  const [selectedProjekt, setSelectedProjekt] = useState<string>("");
  const [studiengaenge, setStudiengaenge] = useState([] as IDataItem[]);
  const [selectedStudiengang, setSelectedStudiengang] = useState<string>("");

  useEffect(() => {
    const handleRoleChange = () => {
      console.log("Dashboard detected role change event");
      const newRoles = getCurrentRole();
      console.log("New roles after event:", newRoles);
      setRoles(newRoles);
    };

    console.log("Dashboard mounted, initial roles:", roles);

    // Check if role was set between mounting and useEffect running
    const currentRoles = getCurrentRole();
    if (currentRoles.length > 0 && roles.length === 0) {
      console.log(
        "Role was set before event listener - updating now:",
        currentRoles,
      );
      setRoles(currentRoles);
    }

    window.addEventListener("role-changed", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("role-changed", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, [roles]);

  // Case-insensitive role checking
  const hasRole = (roleName: string) =>
    roles.some((r) => r.toLowerCase() === roleName.toLowerCase());

  const canCreateProject = hasRole("Freigeber*in") || hasRole("Admin");

  const typeOptions = [
    {
      id: "Neuentwicklung" as const,
      label: "Neuentwicklung",
      description: "Einen neuen Studiengang oder ein Lehrgang entwickeln.",
      icon: <Add24Regular fontSize={38} />,
      iconKey: "add" as const,
    },
    {
      id: "Überarbeitung" as const,
      label: "Überarbeitung",
      description:
        "Einen bestehenden Studiengang oder ein Lehrgang überarbeiten.",
      icon: <Pen24Regular fontSize={38} />,
      iconKey: "edit" as const,
    },
  ];

  const formatOptions = [
    {
      id: "Studiengang" as const,
      label: "Studiengang",
      description: "Bachelor- oder\nMasterstudiengang",
      icon: <HatGraduation24Regular fontSize={38} />,
      iconKey: "studi" as const,
    },
    {
      id: "Lehrgang" as const,
      label: "Lehrgang",
      description: "Akademische\nLehrgänge",
      icon: <Book24Regular fontSize={38} />,
      iconKey: "lehrgang" as const,
    },
  ];

  const revisionOptions = [
    {
      id: "Zyklisch" as const,
      label: "Zyklisch Life-Cycle",
      description:
        "Regelmäßige, planmäßige Überarbeitung im definierten Zyklus.",
      icon: <ArrowSyncRegular fontSize={38} />,
      iconKey: "cycle" as const,
    },
    {
      id: "Azyklisch" as const,
      label: "Azyklisch",
      description:
        "Überarbeitung unabhängig von regelmäßigen Zyklen, bei Bedarf.",
      icon: <NavigationUnreadRegular fontSize={38} />,
      iconKey: "acyclic" as const,
    },
  ];

  const bezugOptions = [
    {
      id: "Projekt" as const,
      label: "Projekt",
      description: "Ein bestehendes Projekt überarbeiten.",
      icon: <CoinStackRegular fontSize={38} />,
      iconKey: "coin" as const,
    },
    {
      id: "Studiengang" as const,
      label: "Studiengang",
      description: "Einen bestehenden Studiengang überarbeiten.",
      icon: <HatGraduation24Regular fontSize={38} />,
      iconKey: "studi" as const,
    },
  ];

  useEffect(() => {
    const getProjekte = async () => {
      if (!dataService) return;
      const response = await dataService.getProjekteAuswahl();
      if (response) {
        setProjekte(response);
      }
    };
    const getStudiengaenge = async () => {
      if (!dataService) return;
      const response = await dataService.getStudiengaengeAuswahl();
      if (response) {
        setStudiengaenge(response);
      }
    };
    getProjekte();
    getStudiengaenge();
  }, [dataService]);

  const renderCard = (
    option: {
      id: string;
      label: string;
      description: string;
      icon: React.ReactNode;
      iconKey: keyof typeof badgeStyles;
    },
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <Card
      key={option.id}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      style={isSelected ? selectedCardStyle : cardBaseStyle}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.iconWrap} style={badgeStyles[option.iconKey]}>
        {option.icon}
      </div>
      <Text as="h3" size={600} className={styles.title}>
        {option.label}
      </Text>
      <Text as="p" size={200} className={styles.description}>
        {option.description}
      </Text>
    </Card>
  );

  return (
    <>
      {canCreateProject ? (
        <>
          {page === "initial" && (
            <div className={styles.root}>
              <div className={styles.content}>
                <section className={styles.section}>
                  <Text as="h2" className={styles.heading}>
                    Typ
                  </Text>
                  <br />
                  <Text as="p" className={styles.helperText}>
                    Wählen Sie den Typ des neuen Projekts.
                  </Text>

                  <div className={styles.grid}>
                    {typeOptions.map((option) =>
                      renderCard(option, selectedType === option.id, () =>
                        setSelectedType(option.id),
                      ),
                    )}
                  </div>
                </section>
                {selectedType === "Neuentwicklung" && (
                  <section className={styles.section}>
                    <Text as="h2" className={styles.heading}>
                      Format
                    </Text>
                    <br />
                    <Text as="p" className={styles.helperText}>
                      Wählen Sie das passende Format für das Projekt.
                    </Text>

                    <div className={styles.formatGrid}>
                      {formatOptions.map((option) =>
                        renderCard(option, selectedFormat === option.id, () =>
                          setSelectedFormat(option.id),
                        ),
                      )}
                    </div>
                  </section>
                )}

                {selectedType === "Überarbeitung" && (
                  <>
                    <section className={styles.section}>
                      <Text as="h2" className={styles.heading}>
                        Art der Überarbeitung
                      </Text>
                      <br />
                      <Text as="p" className={styles.helperText}>
                        Wählen Sie die Art der Überarbeitung.
                      </Text>

                      <div className={styles.grid}>
                        {revisionOptions.map((option) =>
                          renderCard(
                            option,
                            selectedRevisionType === option.id,
                            () => setSelectedRevisionType(option.id),
                          ),
                        )}
                      </div>
                    </section>
                    <section className={styles.section}>
                      <Text as="h2" className={styles.heading}>
                        Bezug auswählen
                      </Text>
                      <br />
                      <Text as="p" className={styles.helperText}>
                        Wählen Sie, worauf sich die Überarbeitung bezieht.
                      </Text>

                      <div className={styles.grid}>
                        {bezugOptions.map((option) =>
                          renderCard(option, selectedBezug === option.id, () =>
                            setSelectedBezug(option.id),
                          ),
                        )}
                      </div>
                    </section>
                    {selectedBezug === "Studiengang" && (
                      <Dropdown
                        style={{ width: "100%" }}
                        placeholder="Studiengang wählen..."
                        value={
                          studiengaenge.find(
                            (p) => p.value === selectedStudiengang,
                          )?.text
                        }
                        selectedOptions={
                          selectedStudiengang ? [selectedStudiengang] : []
                        }
                        onOptionSelect={(_, data) => {
                          setSelectedStudiengang(data.optionValue || "");
                        }}
                      >
                        {studiengaenge.map((p) => (
                          <Option key={p.value} value={p.value} text={p.text}>
                            {p.text}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                    {selectedBezug === "Projekt" && (
                      <Dropdown
                        style={{ width: "100%" }}
                        placeholder="Projekt wählen..."
                        value={
                          projekte.find((p) => p.value === selectedProjekt)
                            ?.text
                        }
                        selectedOptions={
                          selectedProjekt ? [selectedProjekt] : []
                        }
                        onOptionSelect={(_, data) => {
                          setSelectedProjekt(data.optionValue || "");
                        }}
                      >
                        {projekte.map((p) => (
                          <Option key={p.value} value={p.value} text={p.text}>
                            {p.text}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </>
                )}
              </div>

              <div className={styles.actionRow}>
                <Button className={styles.actionButton} onClick={onClose}>
                  Schließen
                </Button>
                <Button
                  appearance="primary"
                  className={styles.actionButton}
                  onClick={() => {
                    setPage("neuanlage");
                    onPageChange?.("neuanlage");
                  }}
                  disabled={
                    selectedType === "Überarbeitung" &&
                    ((selectedBezug === "Studiengang" &&
                      !selectedStudiengang) ||
                      (selectedBezug === "Projekt" && !selectedProjekt))
                  }
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
          {page === "neuanlage" && (
            <Projektdaten
              onClose={onClose}
              typ={selectedType}
              format={selectedFormat}
            />
          )}
        </>
      ) : (
        <p>Sie haben keine Berechtigung, ein neues Projekt anzulegen.</p>
      )}
    </>
  );
};

export default Projektanlage;
